// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import React, { useEffect, useState } from "react";

import { Dropdown } from "../../CaseDetails/Form/Elements/Dropdown";
import { Modal } from "../../Modal/Modal";
import { SharedDatePicker } from "../../shared/SharedDatePicker";
import {
  Checkbox,
  CheckboxContainer,
  CheckboxLabel,
} from "../../shared/styles/CheckboxStyles";
import {
  ButtonRow,
  CancelButton,
  SaveButton,
} from "../../shared/styles/FormButtonStyles";
import { FormLabel } from "../../shared/styles/FormLabel.styles";
import { DatePickerWrapper, dropdownStyles } from "../FormComponents.styles";
import * as ModalStyled from "../ModalStyles";
import {
  AGE_OPTIONS,
  DrugHistory,
  FREQUENCY_OPTIONS,
  FrequencyOfUse,
  FrequencyOfUseLabels,
  METHOD_OPTIONS,
  MethodOfUse,
  MethodOfUseLabels,
  SUBSTANCE_OPTIONS,
  SubstanceType,
  SubstanceTypeLabels,
} from "./constants";

type SelectOption = { label: string; value: string };

const EMPTY_FORM: Omit<DrugHistory, "id"> = {
  substance: null,
  otherSubstanceName: null,
  ageOfRegularUse: null,
  admitsToCurrentUse: false,
  lastUse: null,
  heaviestUse: null,
  method: null,
};

interface DrugHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (history: Omit<DrugHistory, "id">) => Promise<void>;
  initialData?: DrugHistory;
  isEditMode?: boolean;
  clientFirstName: string;
}

export const DrugHistoryModal: React.FC<DrugHistoryModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  isEditMode,
  clientFirstName,
}) => {
  const [formData, setFormData] = useState<Omit<DrugHistory, "id">>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Initialize form with initial data when in edit mode
  useEffect(() => {
    if (isOpen && initialData) {
      setFormData(initialData);
    } else if (isOpen && !initialData) {
      setFormData(EMPTY_FORM);
    }
  }, [isOpen, initialData]);

  const handleClose = () => {
    setFormData(EMPTY_FORM);
    setSaveError(null);
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave(formData);
      handleClose();
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Check if form has any data (at least one field filled)
  const hasData =
    formData.substance ||
    formData.ageOfRegularUse ||
    formData.lastUse ||
    formData.heaviestUse ||
    formData.method;

  const isOtherWithoutName =
    formData.substance === "Other" && !formData.otherSubstanceName;

  return (
    <Modal isOpen={isOpen} hideModal={handleClose} padding={0}>
      <ModalStyled.Container>
        <ModalStyled.Header>
          <ModalStyled.Title>
            {isEditMode ? "Edit Substance Use" : "Add Substance Use"}
          </ModalStyled.Title>
          <ModalStyled.Description>
            We will use this data to generate opportunities for{" "}
            {clientFirstName}. If you don't have this information yet, you can
            add it in later.
          </ModalStyled.Description>
        </ModalStyled.Header>

        <ModalStyled.Form>
          <ModalStyled.Field>
            <FormLabel>Substance</FormLabel>
            <Dropdown
              value={
                formData.substance
                  ? {
                      value: formData.substance,
                      label: SubstanceTypeLabels[formData.substance],
                    }
                  : null
              }
              options={SUBSTANCE_OPTIONS}
              onChange={(option) => {
                const newSubstance =
                  ((option as SelectOption)?.value as SubstanceType) || null;
                setFormData({
                  ...formData,
                  substance: newSubstance,
                  otherSubstanceName:
                    newSubstance === "Other"
                      ? formData.otherSubstanceName
                      : null,
                });
              }}
              placeholder="Select..."
              styles={dropdownStyles}
            />
          </ModalStyled.Field>

          {formData.substance === "Other" && (
            <ModalStyled.Field>
              <FormLabel>Substance Name</FormLabel>
              <ModalStyled.TextInput
                value={formData.otherSubstanceName ?? ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    otherSubstanceName: e.target.value || null,
                  })
                }
                placeholder="Enter substance name"
              />
            </ModalStyled.Field>
          )}

          <ModalStyled.Field>
            <FormLabel>Age of Regular Use</FormLabel>
            <Dropdown
              value={
                formData.ageOfRegularUse
                  ? {
                      value: formData.ageOfRegularUse.toString(),
                      label: formData.ageOfRegularUse.toString(),
                    }
                  : null
              }
              options={AGE_OPTIONS}
              onChange={(option) =>
                setFormData({
                  ...formData,
                  ageOfRegularUse: option
                    ? parseInt((option as SelectOption).value, 10)
                    : null,
                })
              }
              placeholder="Select..."
              styles={dropdownStyles}
            />
          </ModalStyled.Field>

          <ModalStyled.Field>
            <FormLabel>Last Use</FormLabel>
            <ModalStyled.FieldContent>
              <DatePickerWrapper>
                <SharedDatePicker
                  selected={
                    formData.lastUse ? new Date(formData.lastUse) : null
                  }
                  onChange={(date) =>
                    setFormData({ ...formData, lastUse: date ?? null })
                  }
                  placeholder="MM/YYYY"
                  disabled={!!formData.admitsToCurrentUse}
                  monthYearOnly
                />
              </DatePickerWrapper>
              <CheckboxContainer>
                <Checkbox
                  type="checkbox"
                  checked={!!formData.admitsToCurrentUse}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      admitsToCurrentUse: e.target.checked,
                      lastUse: e.target.checked ? null : formData.lastUse,
                    })
                  }
                />
                <CheckboxLabel>
                  Client admits to current use of this substance
                </CheckboxLabel>
              </CheckboxContainer>
            </ModalStyled.FieldContent>
          </ModalStyled.Field>

          <ModalStyled.Field>
            <FormLabel>Heaviest Use</FormLabel>
            <Dropdown
              value={
                formData.heaviestUse
                  ? {
                      value: formData.heaviestUse,
                      label: FrequencyOfUseLabels[formData.heaviestUse],
                    }
                  : null
              }
              options={FREQUENCY_OPTIONS}
              onChange={(option) =>
                setFormData({
                  ...formData,
                  heaviestUse:
                    ((option as SelectOption)?.value as FrequencyOfUse) || null,
                })
              }
              placeholder="Select..."
              styles={dropdownStyles}
            />
          </ModalStyled.Field>

          <ModalStyled.Field>
            <FormLabel>Method</FormLabel>
            <Dropdown
              value={
                formData.method
                  ? {
                      value: formData.method,
                      label: MethodOfUseLabels[formData.method],
                    }
                  : null
              }
              options={METHOD_OPTIONS}
              onChange={(option) =>
                setFormData({
                  ...formData,
                  method:
                    ((option as SelectOption)?.value as MethodOfUse) || null,
                })
              }
              placeholder="Select..."
              styles={dropdownStyles}
            />
          </ModalStyled.Field>
        </ModalStyled.Form>

        <ModalStyled.Footer>
          {saveError && (
            <ModalStyled.ErrorMessage>{saveError}</ModalStyled.ErrorMessage>
          )}
          <ButtonRow>
            <CancelButton onClick={handleClose} disabled={isSaving}>
              Cancel
            </CancelButton>
            <SaveButton
              onClick={handleSave}
              disabled={!hasData || isOtherWithoutName || isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </SaveButton>
          </ButtonRow>
        </ModalStyled.Footer>
      </ModalStyled.Container>
    </Modal>
  );
};
